import React from 'react';
import AssignDropdown from './AssignDropdown';
import ConnectionStatusIndicator from './ConnectionStatusIndicator';

const ChatHeader = React.memo(({
  contact,
  sseConnectionStatus,
  handleContactModalOpen,
  currentAssignee,
  agents,
  agentsLoading,
  agentsError,
  onAssignToMe,
  onUnassign,
  onAssignToAgent,
  messages,
  getConversationStatusButtonProps,
  handleConversationButtonClick,
  showConversationPopup,
  setShowConversationPopup,
  handleCloseConversation,
  conversationActionLoading
}) => (
  <div style={{
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: '10px',
    padding: '20px 20px 15px',
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #D0D5DD',
    borderRadius: '0px 20px 0px 0px'
  }}>
    {/* Avatar */}
    <div style={{
      position: 'relative',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'linear-gradient(180deg, rgba(45, 87, 168, 1) 0%, rgba(45, 168, 88, 0) 100%)',
      padding: '3px'
    }}>
      <div style={{
        width: '34px',
        height: '34px',
        borderRadius: '50%',
        backgroundColor: '#F2F4F7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {contact.avatar ? (
          <img
            src={contact.avatar}
            alt={contact.name}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
              fill="#98A2B3"
            />
            <path
              d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z"
              fill="#98A2B3"
            />
          </svg>
        )}
      </div>
    </div>

    {/* Contact Details */}
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      flex: '1'
    }}>
      {/* Contact Name/Phone Row */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: '16px'
      }}>
        {/* Connection Status - positioned at the top right */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px'
        }}>
          <ConnectionStatusIndicator sseConnectionStatus={sseConnectionStatus} />
        </div>
        {/* Name and Phone Column */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'stretch',
          alignSelf: 'stretch',
          gap: '2px',
          flex: '1'
        }}>
          {/* Contact Name */}
          <span style={{
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '16px',
            lineHeight: '1.5em',
            letterSpacing: '0.25%',
            color: '#344054'
          }}>
            {contact.isContact && contact.contactData?.displayName
              ? contact.contactData.displayName
              : contact.name || contact.phone || contact.phoneNumber || 'Unknown'}
          </span>

          {/* Phone Number and Edit Button Row */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'stretch',
            gap: '10px'
          }}>
            <span style={{
              fontFamily: 'Poppins',
              fontWeight: '400',
              fontSize: '14px',
              lineHeight: '1.5em',
              color: '#737C8A'
            }}>
              {contact.phone || contact.phoneNumber || 'Unknown'}
            </span>
            <button
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                padding: '2px 8px',
                backgroundColor: 'transparent',
                border: contact.isContact ? '1px solid #0BA9E0' : '1px solid #D0D5DD',
                borderRadius: '4px',
                fontFamily: 'Poppins',
                fontWeight: '400',
                fontSize: '14px',
                color: contact.isContact ? '#0BA9E0' : '#344054',
                cursor: 'pointer'
              }}
              onClick={handleContactModalOpen}
            >
              {contact.isContact ? 'Edit contact' : 'Add contact'}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Action Buttons */}
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      gap: '8px'
    }}>
      {/* Assign Dropdown */}
      <AssignDropdown
        conversationId={messages.length > 0 ? messages[0].conversation_id : null}
        currentAssignee={currentAssignee}
        agents={agents}
        agentsLoading={agentsLoading}
        agentsError={agentsError}
        onAssignToMe={onAssignToMe}
        onUnassign={onUnassign}
        onAssignToAgent={onAssignToAgent}
        disabled={!messages.length > 0}
      />

      {/* Conversation Status Button */}
      {(() => {
        const buttonProps = getConversationStatusButtonProps();
        return (
          <div style={{ position: 'relative' }}>
            <button
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px 10px 10px',
                width: '40px',
                height: '40px',
                backgroundColor: buttonProps.disabled ? '#F9FAFB' : '#FFFFFF',
                border: `1px solid ${buttonProps.disabled ? '#E4E7EC' : '#D0D5DD'}`,
                borderRadius: '4px',
                cursor: buttonProps.disabled ? 'not-allowed' : 'pointer',
                boxShadow: buttonProps.disabled ? 'none' : '0px 1px 2px rgba(16, 24, 40, 0.05)',
                opacity: buttonProps.disabled ? 0.6 : 1,
                transition: 'all 0.2s ease-in-out'
              }}
              onClick={handleConversationButtonClick}
              disabled={buttonProps.disabled}
              title={buttonProps.title}
            >
              {buttonProps.loading ? (
                // Loading spinner
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #E4E7EC',
                  borderTop: '2px solid #38C669',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : buttonProps.isOpen ? (
                // Close icon (X) for open conversations
                <svg width="17" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_8152_12769)">
                    <path d="M5.2538 3.18832C5.2538 3.77601 5.72943 4.25165 6.31713 4.25165H12.2538L3.43593 13.0546C3.02006 13.4705 3.02006 14.1453 3.43593 14.5637C3.85179 14.9796 4.52914 14.9796 4.94501 14.5637L13.7505 5.74828V11.685C13.7505 12.2727 14.2261 12.7483 14.8138 12.7483C15.4015 12.7483 15.8771 12.2727 15.8771 11.685V3.18832C15.8771 2.60062 15.4015 2.12499 14.8138 2.12499H6.31464C5.72943 2.12499 5.2538 2.60062 5.2538 3.18832Z" fill="#38C669" />
                  </g>
                  <defs>
                    <clipPath id="clip0_8152_12769">
                      <rect width="12.75" height="12.75" fill="white" transform="matrix(0 -1 1 0 3.125 14.875)" />
                    </clipPath>
                  </defs>
                </svg>
              ) : (
                // Stop icon for closed conversations - Matching Figma design exactly
                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g transform="translate(1.42, 1.42)">
                    <path 
                      d="M10.58 3.58L3.58 10.58M3.58 3.58L10.58 10.58" 
                      stroke="#737C8A" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              )}
            </button>

            {/* Conversation Actions Dropdown */}
            {showConversationPopup && buttonProps.isOpen && (
              <>
                {/* Backdrop to close dropdown when clicking outside */}
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999
                  }}
                  onClick={() => setShowConversationPopup(false)}
                />

                {/* Dropdown Menu */}
                <div style={{
                  position: 'absolute',
                  top: '48px',
                  right: '0px',
                  zIndex: 1000,
                  borderRadius: '16px',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0px 3.75px 11px 0px rgba(0, 0, 0, 0.19), 0px 0.5px 5px 0px rgba(0, 0, 0, 0.04)',
                  overflow: 'hidden'
                }}>
                  {/* Close Conversation Option */}
                  <button
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 16px',
                      backgroundColor: conversationActionLoading ? 'rgba(247, 102, 89, 0.6)' : '#F76659',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: conversationActionLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      textAlign: 'left',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={handleCloseConversation}
                    disabled={conversationActionLoading}
                  >
                    {conversationActionLoading ? (
                      // Loading spinner
                      <div style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid #FFFFFF',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    ) : (
                      // Close icon (white stop icon from Figma)
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                          stroke="#FFFFFF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    <span style={{
                      fontFamily: 'Poppins',
                      fontWeight: '500',
                      fontSize: '14px',
                      lineHeight: '1.5em',
                      letterSpacing: '0.25%',
                      color: conversationActionLoading ? 'rgba(255, 255, 255, 0.6)' : '#FFFFFF'
                    }}>
                      Close Conversation
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })()}
    </div>
  </div>
));

ChatHeader.displayName = 'ChatHeader';

export default ChatHeader;
